import { useCallback, useState } from 'react';

import { toast } from 'sonner';

import {
  useManuallyAssignContentLabel,
  useRemoveContentLabelManualAssignments,
} from '../../../../graphql/contentLabels/mutationManualAssignment';
import { extractErrorMessage } from '../../../../utils/errorFormatting';

/**
 * Content Labels v2 on the portfolio Content tab — the WRITES behind every
 * per-category cell, mounted ONCE for the whole table.
 *
 * Once, because 50 rows x N categories would otherwise instantiate a hundred
 * `useMutation`s per page. The cells are pure presentation and hand their ids
 * up; the busy flag is category-wide, which is also exactly the granularity
 * `RowLabelPicker`'s `busy` prop wants.
 *
 * It calls the shared graphql modules directly rather than `labelsRepository`:
 * that seam mounts eight mutations plus a 500-row portfolio query, answers in
 * the Settings domain, and the dependency would run
 * `routes/portfolio -> routes/settings`.
 *
 * THE FAILURE PATH IS THE POINT. Both mutations carry
 * `CONTENT_LABEL_MUTATION_CONTEXT = { skipGlobalErrorToast: true }` AND reject
 * rather than returning an outcome, so a cell without a catch here would show
 * the user a click that does nothing, forever. Every failure is caught, said
 * once, and never rethrown into a `onSelect` handler that cannot do anything
 * with it.
 */
export type ContentLabelAssignments = {
  /**
   * The category a write is in flight for, or null. Category-wide on purpose:
   * one pick can move several rows between stages, so every picker in the
   * column holds until the answer lands.
   */
  pendingCategoryId: string | null;
  /** Apply one label to these contents in this category. */
  assign: (contentLabelCategoryId: string, contentLabelCategoryValueId: string, contentIds: string[]) => Promise<void>;
  /**
   * Clear whatever a person assigned in this category. CATEGORY-WIDE — the
   * input carries no value id — and reached from "Remove manual assignment".
   */
  remove: (contentLabelCategoryId: string, contentIds: string[]) => Promise<void>;
};

export function useContentLabelAssignments(): ContentLabelAssignments {
  const [manuallyAssignContentLabel] = useManuallyAssignContentLabel();
  const [removeContentLabelManualAssignments] = useRemoveContentLabelManualAssignments();
  const [pendingCategoryId, setPendingCategoryId] = useState<string | null>(null);

  const run = useCallback(async (contentLabelCategoryId: string, write: () => Promise<unknown>): Promise<void> => {
    setPendingCategoryId(contentLabelCategoryId);
    try {
      await write();
    } catch (error) {
      toast.error(extractErrorMessage(error));
    } finally {
      // In `finally`, so a rejection cannot leave the whole column frozen: the
      // picker refuses to open while `busy`, and nothing else would clear it.
      setPendingCategoryId(null);
    }
  }, []);

  const assign = useCallback(
    (contentLabelCategoryId: string, contentLabelCategoryValueId: string, contentIds: string[]) => {
      // `toAssignableContentIds([])` is `unassignable`, so the mutation would
      // reject and toast "no contents are selected" for a click nobody made.
      if (!contentIds?.length) return Promise.resolve();

      // A `false` answer RESOLVES, and the hook skips its eviction and refetch on
      // it — so it is thrown onto the same path a rejection takes, in the copy
      // the Settings commands use for the same answer.
      return run(contentLabelCategoryId, async () => {
        const { data } = await manuallyAssignContentLabel({
          contentLabelCategoryId,
          contentLabelCategoryValueId,
          contentIds,
        });
        if (!data?.manuallyAssignContentLabel) throw new Error('The label could not be assigned.');
      });
    },
    [manuallyAssignContentLabel, run],
  );

  const remove = useCallback(
    (contentLabelCategoryId: string, contentIds: string[]) => {
      if (!contentIds?.length) return Promise.resolve();

      return run(contentLabelCategoryId, async () => {
        const { data } = await removeContentLabelManualAssignments({ contentLabelCategoryId, contentIds });
        if (!data?.removeContentLabelManualAssignments) throw new Error('The labels could not be removed.');
      });
    },
    [removeContentLabelManualAssignments, run],
  );

  return { pendingCategoryId, assign, remove };
}
